import Phaser from 'phaser';

type StrokePreviewerConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	color: string;
};

const ANIMATION = {
	lineWidth: 38,
	windowLength: 600,
	speed: 2400,
	sampleSpacing: 8,
} as const;

export class StrokePreviewer {
	private readonly scene: Phaser.Scene;
	private readonly graphics: Phaser.GameObjects.Graphics;
	private readonly color: number;
	private tween?: Phaser.Tweens.Tween;
	private playbackId = 0;

	constructor({ scene, parent, color }: StrokePreviewerConfig) {
		this.scene = scene;
		this.color = Phaser.Display.Color.HexStringToColor(color).color;
		this.graphics = scene.add.graphics();
		parent.add(this.graphics);
	}

	play(path: Phaser.Curves.Path) {
		this.stop();
		const playbackId = this.playbackId;
		const pathLength = path.getLength();

		if (pathLength <= 0) {
			return;
		}

		const travelDistance = pathLength + ANIMATION.windowLength;
		this.render(path, pathLength, 0);
		this.tween = this.scene.tweens.addCounter({
			from: 0,
			to: travelDistance,
			duration: (travelDistance / ANIMATION.speed) * 1000,
			ease: 'Linear',
			onUpdate: (tween) => {
				if (playbackId !== this.playbackId) return;
				this.render(path, pathLength, tween.getValue() ?? 0);
			},
			onComplete: () => {
				if (playbackId !== this.playbackId) return;
				this.graphics.clear();
				this.tween = undefined;
			},
		});
	}

	stop() {
		this.playbackId++;
		this.tween?.remove();
		this.tween = undefined;
		this.graphics.clear();
	}

	private render(path: Phaser.Curves.Path, pathLength: number, distance: number) {
		const startDistance = Phaser.Math.Clamp(distance - ANIMATION.windowLength, 0, pathLength);
		const endDistance = Phaser.Math.Clamp(distance, 0, pathLength);
		this.graphics.clear();

		if (startDistance >= pathLength) {
			return;
		}

		const segmentLength = endDistance - startDistance;
		const intervalCount = Math.max(1, Math.ceil(segmentLength / ANIMATION.sampleSpacing));
		const points = Array.from({ length: intervalCount + 1 }, (_unused, index) => {
			const pointDistance = startDistance + (segmentLength * index) / intervalCount;
			return path.getPoint(pointDistance / pathLength);
		});

		this.graphics.lineStyle(ANIMATION.lineWidth, this.color).strokePoints(points);
		const radius = ANIMATION.lineWidth / 2;
		this.graphics.fillStyle(this.color).fillCircle(points[0].x, points[0].y, radius);
		const end = points.at(-1)!;
		this.graphics.fillCircle(end.x, end.y, radius);
	}
}
